import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  Database, Table, HardDrive, Download, 
  FileText, Loader2 
} from 'lucide-react';

const DatabasePage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('explorer');
  const [selectedTable, setSelectedTable] = useState(null);
  const [page, setPage] = useState(1);

  // --- QUERIES ---
  const { data: tables = [] } = useQuery({
    queryKey: ['dbSchema'],
    queryFn: () => axiosClient.get('/admin/database/schema')
  });

  const { data: tableData, isLoading: isTableLoading } = useQuery({
    queryKey: ['tableData', selectedTable, page],
    queryFn: () => axiosClient.get(`/admin/database/table/${selectedTable}`, { params: { page } }),
    enabled: !!selectedTable
  });

  const { data: backups = [] } = useQuery({
    queryKey: ['backups'],
    queryFn: () => axiosClient.get('/admin/database/backups'),
    enabled: activeTab === 'backup'
  });

  // --- MUTATIONS ---
  const backupMutation = useMutation({
    mutationFn: () => axiosClient.post('/admin/database/backup'),
    onSuccess: () => {
        queryClient.invalidateQueries(['backups']);
        alert("Backup Created Successfully!");
    },
    onError: () => alert("Backup Failed.")
  });

  // --- HANDLERS ---
  const handleDownload = (filename) => {
      // Direct browser download
      window.location.href = `https://api.airssecurity.com/api/v1/admin/database/download/${filename}`;
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center">
            <Database className="w-8 h-8 text-blue-600 mr-3" />
            Database Management
          </h1>
          <p className="text-sm text-slate-500">Explore data and manage backups.</p>
        </div>
        <div className="bg-slate-100 p-1 rounded-lg flex text-sm font-medium">
           <button onClick={() => setActiveTab('explorer')} className={`px-4 py-2 rounded-md ${activeTab==='explorer' ? 'bg-white shadow text-blue-600' : 'text-slate-500'}`}>Data Explorer</button>
           <button onClick={() => setActiveTab('backup')} className={`px-4 py-2 rounded-md ${activeTab==='backup' ? 'bg-white shadow text-purple-600' : 'text-slate-500'}`}>Backups</button>
        </div>
      </div>

      {/* --- TAB: EXPLORER --- */}
      {activeTab === 'explorer' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-200px)]">
           
           {/* Sidebar: Table List */}
           <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
              <div className="p-4 bg-slate-50 border-b border-slate-100 font-bold text-slate-700 text-sm">
                  Tables ({tables.length})
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                  {tables.map(t => (
                      <button 
                        key={t.table_name}
                        onClick={() => { setSelectedTable(t.table_name); setPage(1); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex justify-between items-center ${selectedTable === t.table_name ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-slate-600 hover:bg-slate-50'}`}
                      >
                         <span className="truncate">{t.table_name}</span>
                         <span className="text-[10px] bg-slate-200 px-1.5 rounded-full text-slate-600">{t.row_count}</span>
                      </button>
                  ))}
              </div>
           </div>

           {/* Main: Data Grid */}
           <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
              {selectedTable ? (
                  <>
                    <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="font-bold text-slate-800 flex items-center"><Table className="w-4 h-4 mr-2"/> {selectedTable}</h3>
                        <div className="flex gap-2">
                           <button disabled={page===1} onClick={() => setPage(p => p-1)} className="px-3 py-1 border rounded text-xs disabled:opacity-50">Prev</button>
                           <span className="text-xs py-1">Page {page}</span>
                           <button onClick={() => setPage(p => p+1)} className="px-3 py-1 border rounded text-xs">Next</button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto custom-scrollbar p-1">
                        {isTableLoading ? (
                            <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2"/> Fetching Data...</div>
                        ) : (
                            <table className="w-full text-left border-collapse text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                    <tr>
                                        {tableData?.columns?.map(col => (
                                            <th key={col} className="px-4 py-2 border-b font-medium text-slate-500 whitespace-nowrap">{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData?.rows?.map((row, i) => (
                                        <tr key={i} className="hover:bg-slate-50">
                                            {tableData.columns.map(col => (
                                                <td key={col} className="px-4 py-2 border-b text-slate-600 whitespace-nowrap max-w-xs truncate">{String(row[col])}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                  </>
              ) : (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400">
                      <HardDrive className="w-16 h-16 mb-4 opacity-20" />
                      <p>Select a table to view data.</p>
                  </div>
              )}
           </div>
        </div>
      )}

      {/* --- TAB: BACKUP --- */}
      {activeTab === 'backup' && (
         <div className="max-w-4xl mx-auto space-y-8">
             <div className="bg-gradient-to-r from-slate-900 to-purple-900 text-white p-8 rounded-2xl flex justify-between items-center shadow-lg">
                 <div>
                    <h2 className="text-2xl font-bold mb-2">Create Full Backup</h2>
                    <p className="text-purple-200 text-sm">Dump all SQL data, schema, and relations to a downloadable file.</p>
                 </div>
                 <button 
                   onClick={() => backupMutation.mutate()}
                   disabled={backupMutation.isLoading}
                   className="bg-white text-purple-900 px-6 py-3 rounded-xl font-bold shadow-lg hover:bg-purple-50 flex items-center"
                 >
                    {backupMutation.isLoading ? <Loader2 className="animate-spin mr-2"/> : <Download className="w-5 h-5 mr-2"/>} 
                    Generate Backup
                 </button>
             </div>

             <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                 <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-slate-700">Available Backups</div>
                 <div className="divide-y divide-slate-100">
                     {backups.map((b) => (
                         <div key={b.filename} className="p-4 flex justify-between items-center hover:bg-slate-50">
                             <div className="flex items-center">
                                 <div className="p-2 bg-blue-50 text-blue-600 rounded-lg mr-4"><FileText className="w-5 h-5"/></div>
                                 <div>
                                     <p className="font-bold text-slate-800">{b.filename}</p>
                                     <p className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString()} • {b.size_mb} MB</p>
                                 </div>
                             </div>
                             <button onClick={() => handleDownload(b.filename)} className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center">
                                 <Download className="w-4 h-4 mr-1"/> Download
                             </button>
                         </div>
                     ))}
                     {backups.length === 0 && <div className="p-8 text-center text-slate-400">No backups found.</div>}
                 </div>
             </div>
         </div>
      )}

    </div>
  );
};

export default DatabasePage;